import React from 'react';
import { PaymentReturn } from '../clients/rust-api-client';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import { Text } from '../common/Catalyst/text';
import {
  formatBillingDate,
  formatEpochSeconds,
  formatMoney,
} from '../utils/billingHelpers';
import { paymentMethod, paymentStatus } from './enumLabels';

interface Props {
  payment: PaymentReturn;
  /** From the HOA's policy — changes what an overpayment means. */
  allowsCreditBalances: boolean;
  onMatch?: (p: PaymentReturn) => void;
  onReturn?: (p: PaymentReturn) => void;
  onRefund?: (p: PaymentReturn) => void;
  /** Charge the returned-payment fee to a payment the chargeback job
   *  reversed without one. Same right as returning it. */
  onAssessFee?: (p: PaymentReturn) => void;
}

/**
 * One payment, with what it paid and what it did not.
 *
 * **An overpayment is held money, not a negative balance.** The
 * remainder sits unapplied on this row: it is the association's to
 * refund, and the ledger balance never went below zero. Where the HOA
 * allows credit balances it would have posted forward as a credit
 * instead — so the wording has to follow the policy rather than assume
 * one.
 *
 * **A returned payment keeps its applications, marked reversed.** They
 * are how anybody answers "which charges did this bounced cheque pay?",
 * which is the first question on a collections call — so they stay
 * visible rather than being cleared away.
 */
const PaymentCard: React.FC<Props> = ({
  payment,
  allowsCreditBalances,
  onMatch,
  onReturn,
  onRefund,
  onAssessFee,
}) => {
  const held = payment.amount_unapplied;
  const isHolding =
    payment.status === 'overpaid' || payment.status === 'unmatched';
  const live = payment.applications.filter((a) => !a.reversed_at);
  const reversed = payment.applications.filter((a) => a.reversed_at);

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg font-semibold text-zinc-900 dark:text-white">
              {formatMoney(payment.amount)}
            </span>
            <Badge color={paymentStatus.color(payment.status)}>
              {paymentStatus.label(payment.status)}
            </Badge>
            <Text className="text-sm">
              {paymentMethod.label(payment.method)}
              {payment.check_number ? ` #${payment.check_number}` : ''} ·{' '}
              {formatBillingDate(payment.received_date)}
            </Text>
          </div>
          <Text className="mt-1 text-sm">
            {payment.home_label ?? 'Not matched to a home'}
            {payment.payer_name ? ` · ${payment.payer_name}` : ''}
            {payment.account_number
              ? ` · account ${payment.account_number}`
              : ''}
            {payment.batch_id ? ` · batch ${payment.batch_id}` : ''}
          </Text>
        </div>

        <div className="flex flex-wrap gap-2">
          {onMatch && payment.status === 'unmatched' && (
            <Button color="sky" onClick={() => onMatch(payment)}>
              Match to a home
            </Button>
          )}
          {onRefund && payment.status === 'overpaid' && held !== '0.00' && (
            <Button outline onClick={() => onRefund(payment)}>
              Refund {formatMoney(held)}
            </Button>
          )}
          {onReturn &&
            (payment.status === 'applied' || payment.status === 'overpaid') && (
              <Button plain onClick={() => onReturn(payment)}>
                Returned (NSF)
              </Button>
            )}
          {onAssessFee &&
            payment.status === 'returned' &&
            !payment.nsf_fee_entry_id && (
              <Button outline onClick={() => onAssessFee(payment)}>
                Charge the fee
              </Button>
            )}
        </div>
      </div>

      {isHolding && held !== '0.00' && (
        <div className="mt-3 rounded-md border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/40 p-3">
          <Text>
            <strong>{formatMoney(held)} is held on this payment.</strong>{' '}
            {payment.status === 'unmatched'
              ? 'It arrived on an account number that could not be resolved, so none of it has been applied. Match it to a home and it will apply itself.'
              : allowsCreditBalances
                ? 'More than was owed. This association allows credit balances, so it can be carried forward against the next charge — or refunded.'
                : 'More than was owed. This association does not allow credit balances, so the remainder stays here rather than becoming a negative balance: it is the homeowner’s money, and it can be refunded.'}
          </Text>
        </div>
      )}

      {payment.status === 'returned' && (
        <div className="mt-3 rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-3">
          <Text>
            This payment bounced. Everything it had paid is open again —{' '}
            {reversed.length}{' '}
            {reversed.length === 1 ? 'application was' : 'applications were'}{' '}
            reversed.
            {payment.nsf_fee_entry_id
              ? ' An NSF fee was charged to the home’s ledger.'
              : ' No NSF fee was charged — a bounce the bank reported is reversed without one, and the fee is a separate decision.'}
          </Text>
        </div>
      )}

      {payment.applications.length > 0 && (
        <div className="mt-3">
          <Text className="text-sm font-medium">
            {payment.status === 'returned'
              ? 'What it had paid'
              : 'What it paid'}
          </Text>
          <ul className="mt-1 space-y-1">
            {[...live, ...reversed].map((a) => (
              <li key={a.id} className="text-sm">
                <span
                  className={
                    a.reversed_at
                      ? 'line-through text-zinc-500 dark:text-zinc-400'
                      : ''
                  }
                >
                  {formatMoney(a.amount)}
                </span>{' '}
                <span className="text-zinc-500 dark:text-zinc-400">
                  {a.reversed_at
                    ? `— reversed ${formatEpochSeconds(a.reversed_at)}`
                    : `applied ${formatEpochSeconds(a.applied_at)}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {payment.notes && <Text className="mt-3 text-sm">{payment.notes}</Text>}
    </div>
  );
};

export default PaymentCard;
